import { useCallback, useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Nav, Navbar, NavItem } from 'reactstrap'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import classnames from 'classnames'

import { setCurrentTab } from '../../../actions/commonActions'
import { deleteProgram, getDashboardProgram } from '../../../actions/programActions'
import { resetDashboard, selectAllEnrollments } from '../../../reducers/programDashboardReducer'
import { checkLicensePermission } from '../../../utility/communityLicense'
import Loader from '../../common/loader/legacy-loaders/Op36Loader-web'
import LiveModal from '../ProgramBuilder/Modals/LiveModal'
import ConfirmationModal from '../../common/ConfirmationModal'
import Header from './Header'
import AddStudentsModal from './add-students-modal/AddStudentsModal'
import AddStudentsConfirmationModal from './add-students-modal/AddStudentsConfirmationModal'
import LockError from '../../common/LockError'

import classes from './ProgramDashboardManager.module.scss'
import * as commonActions from '../../../actions/commonActions'
import WaitlistStudentsConfirmationModal from './add-students-modal/WaitlistStudentsConfirmationModal'

const ProgramDashboardManager = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const dispatch = useDispatch()

  const licenseType = useSelector(state => state.user.current_user.communities[0].license_type)
  const enrollments = useSelector(selectAllEnrollments)
  const { program, errors } = useSelector(state => state.programDashboard)

  const [isLoading, setIsLoading] = useState(true)
  const [isAddStudentsModalOpen, setAddStudentsModalOpen] = useState(false)
  const [isWaitlistStudentsModalOpen, setIsWaitlistStudentsModalOpen] = useState(false)
  const [isLiveModalOpen, setLiveModalOpen] = useState(false)
  const [isLoadingDeleteProgram, setIsLoadingDeleteProgram] = useState(false)
  const [isDeleteStudentConfirmationModalOpen, setIsDeleteStudentConfirmationModalOpen] = useState(false)
  const [isAddStudentsConfirmationModalOpen, setIsAddStudentsConfirmationModalOpen] = useState(false)
  const [isWaitlistStudentsConfirmationModalOpen, setIsWaitlistStudentsConfirmationModalOpen] = useState(false)

  const programIdParams = params.id
  const withLiveModal = location.state?.showLiveModal

  const hasUnapprovedEnrollments = useMemo(() => (
    enrollments.filter(enrollment => enrollment.status === 'applied').length > 0
  ), [enrollments])

  const handleOpenAddStudentsModal = useCallback(() => setAddStudentsModalOpen(true), [])

  const handleOpenWaitlistStudentsModal = useCallback(() => setIsWaitlistStudentsModalOpen(true), [])

  const handleDeleteProgram = async () => {
    try {
      setIsLoadingDeleteProgram(true)
      await dispatch(deleteProgram(program.id)).unwrap()
      navigate('/programs')
    } catch (err) {
      toast.error(t('common.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT })
      setIsDeleteStudentConfirmationModalOpen(false)
      setIsLoadingDeleteProgram(false)
    }
  }

  const handleCloseConfirmDeleteModal = () => setIsDeleteStudentConfirmationModalOpen(false)

  const withAddStudentsAction = checkLicensePermission(licenseType, ['standard_legacy'])
    && (program?.status === 'active' || program?.status === 'future')

  const tabsData = useMemo(() => ([
    { name: t('programs.manage.tabs.overview'), route: 'overview' },
    ...program?.withSchedule ? [{ name: t('programs.manage.tabs.schedule'), route: 'schedule' }] : [],
    ...program?.lessonPlan ? [{ name: t('programs.manage.tabs.lessonPlan'), route: 'lesson-plan' }] : [],
    ...program?.series.length > 0 ? [{ name: t('programs.manage.tabs.events'), route: 'events' }] : [],
    { name: t('programs.manage.tabs.reports'), route: 'reports' },
    {
      name: t('programs.manage.tabs.roster'),
      route: 'roster',
      withDot: program?.hasManualEnrollmentsApproval && hasUnapprovedEnrollments,
    },
  ]), [t, program, hasUnapprovedEnrollments])

  useEffect(() => {
    dispatch(commonActions.showSideBar(true))
    dispatch(commonActions.showNavBar(true))
    dispatch(setCurrentTab('Programs'))
    dispatch(getDashboardProgram(programIdParams)).unwrap()
      .catch((e) => {
        if (e.status !== 404 && e.status !== 403) {
          toast.error(t('common.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT })
        }
      })
      .finally(() => setIsLoading(false))

    return () => dispatch(resetDashboard())
  }, [dispatch, programIdParams, t])

  useEffect(() => {
    if (withLiveModal) {
      setLiveModalOpen(true)
    }
  }, [dispatch, programIdParams, withLiveModal])

  useEffect(() => {
    const navigateToCorrectTab = () => {
      if (program.hasManualEnrollmentsApproval && hasUnapprovedEnrollments) {
        navigate('roster', { replace: true, state: location.state })
      } else {
        navigate('overview', { replace: true, state: location.state })
      }
    }

    if (program) {
      if (location.pathname === `/programs/${program?.id}`) {
        navigateToCorrectTab()
      } else {
        const parts = location.pathname.split('/')
        const tabRoute = parts[parts.length - 1]
        if (!tabsData.some(item => item.route === tabRoute)) {
          navigateToCorrectTab()
        }
      }
    }
  }, [hasUnapprovedEnrollments, location.pathname, navigate, program, tabsData])

  useEffect(() => {
    if (errors?.status === 404 || errors?.status === 403) {
      dispatch(commonActions.showSideBar(false))
      dispatch(commonActions.showNavBar(false))
    }
  }, [dispatch, errors])

  if (isLoading) {
    return (
      <Loader message={t('programs.manage.loadingProgram')} />
    )
  }

  if (errors?.status === 404 || errors?.status === 403) {
    return (
      <LockError
        className={classes.errorPage}
        titleText={t('programs.common.programNotFound')}
        linkAttributes={{
          linkMessage: t('programs.common.browseOtherPrograms'),
          navigateTo: '/programs',
        }}
      >
        <p>{t('programs.common.programDoesntExist')}</p>
      </LockError>
    )
  }

  return (
    <>
      <div className={classnames('d-flex flex-column', classes.dashboardManagerPage)}>
        <Header
          isAddStudentsModalOpen={isAddStudentsModalOpen}
          isLoadingDeleteProgram={isLoadingDeleteProgram}
          handleOpenAddStudentsModal={handleOpenAddStudentsModal}
          handleDeleteProgramClick={() => setIsDeleteStudentConfirmationModalOpen(true)}
          withAddStudentsAction={withAddStudentsAction}
        />
        <Navbar className={classes.navbar}>
          <Nav className={classes.nav} tabs>
            {tabsData.map((item, index) => (
              <NavItem key={index} className={classes.navItem}>
                <NavLink
                  to={item.route}
                  className={({ isActive }) => classnames(classes.navLink, { [classes.active]: isActive })}
                >
                  {item.name}
                  {item.withDot && <div className={classes.dot}/>}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </Navbar>
        <main className={classes.main}>
          <Outlet context={
            { onOpenAddStudentsModal: handleOpenAddStudentsModal, withAddStudentsAction: withAddStudentsAction, handleOpenWaitlistStudentsModal }
          } />
        </main>
      </div>
      {withAddStudentsAction && (
        <>
          {isAddStudentsModalOpen && (
            <AddStudentsModal
              title={t('programs.common.addStudents')}
              isOpen={isAddStudentsModalOpen}
              onClose={() => setAddStudentsModalOpen(false)}
              onProceed={() => setIsAddStudentsConfirmationModalOpen(true)}
            />
          )}
          <AddStudentsConfirmationModal
            isOpen={isAddStudentsConfirmationModalOpen}
            onClose={() => setIsAddStudentsConfirmationModalOpen(false)}
          />
        </>
      )}
      {isWaitlistStudentsModalOpen && (
        <AddStudentsModal
          toWaitlist
          title={t('programs.manage.addStudentsToWaitlist')}
          isOpen={isWaitlistStudentsModalOpen}
          onClose={() => setIsWaitlistStudentsModalOpen(false)}
          onProceed={() => setIsWaitlistStudentsConfirmationModalOpen(true)}
        />
      )}
      {isWaitlistStudentsConfirmationModalOpen && (
        <WaitlistStudentsConfirmationModal
          isOpen={isWaitlistStudentsConfirmationModalOpen}
          onClose={() => setIsWaitlistStudentsConfirmationModalOpen(false)}
        />
      )}

      {withLiveModal && isLiveModalOpen && (
        <LiveModal
          program={program}
          showModal={isLiveModalOpen}
          onClose={() => setLiveModalOpen(false)}
        />
      )}
      <ConfirmationModal
        showConfirmationModal={isDeleteStudentConfirmationModalOpen}
        closeConfirmationModal={handleCloseConfirmDeleteModal}
        confirmationMessage={t('programs.manage.deleteProgramConfirmation')}
        confirmationDenied={handleCloseConfirmDeleteModal}
        confirmationProceed={handleDeleteProgram}
      />
    </>
  )
}

export default ProgramDashboardManager
